import { Paper } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import AdView from '../../components/ui/AdView';
import Emoji from '../../components/ui/Emoji';
import { Ad_RefreshKey, DialogMessageState, ToastMessageState } from '../../recoil/states';
import { asyncReqCommunityBoardReg, CommunityBoardRegActions } from '../../reducers/CommunityBoardRegReducer';
import { AppDispatch, RootState } from '../../Store';
import CommonUtil from '../../utils/CommonUtil';
import { useKeyboardAwareFocus } from '../../hooks/useKeyboardAwareFocus';

const BoardReg = () => {

    const CommunityBoardRegReducer_Result = useSelector((state: RootState) => state.CommunityBoardRegReducer); // 게시물 등록 처리를 위한 state
    const { NICKNAME } = useSelector((state: RootState) => state.LoginBtnsReducer.asyncIsLogin); // '로그인 / 로그아웃' 버튼 표시 처리를 위한 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다일얼로그 메세지 요청 정보를 갖는 state
    const setToastMessageState = useSetRecoilState(ToastMessageState); // 토스트 메세지 요청 정보를 갖는 state
    const ad_RefreshKey = useRecoilValue(Ad_RefreshKey); // 광고 UI 랜더링 처리를 위해 사용되는 state
    const dispatch = useDispatch<AppDispatch>();

    const [iptTitleVal, setIptTitleVal] = useState(""); // 제목 입력박스 State
    const [iptNicknameVal, setIptNicknameVal] = useState(""); // 별명 입력박스 State
    const [iptAnonymousVal, setIptAnonymousVal] = useState(true); // 별명 익명으로 표시 여부 체크박스 State

    const refBtnReg = useRef<HTMLAnchorElement>(null); // 댓글등록 링크 버튼 참조 객체

    // 입력박스에 포커스가 들어가면 소프트키패드 상단으로 이동 / 해당 입력박스가 가시영역에서 사라지면 소프트키패드 숨김 처리해주는 hook
    useKeyboardAwareFocus({ minVisiblePx: 24, autoBlurIfOutOfView: true });

    useEffect(() => {
        CommonUtil.scrollTo(0, 0, 0); // 최상단으로 스크롤바 자동 이동
        (window as any).removePreviewFile("One"); // 첨부파일 초기화
    }, []);

    useEffect(() => {
        if (CommunityBoardRegReducer_Result.status === "LOADING") {
            // 게시물 등록 > 통신 중 일 경우
        }
        else if (CommunityBoardRegReducer_Result.status === "FAIL") {
            // 게시물 등록 > 통신 실패된 경우

            const message = "게시물 등록 실패, 통신 처리 중 알 수 없는 오류가 발생하였습니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
        }
        else if (CommunityBoardRegReducer_Result.status === "COMPLETE") {
            // 게시물 등록 > 통신 성공된 경우

            dispatch(CommunityBoardRegActions.setInitStatus()); // 여기 영역이 중복 실행되지 않도록 초기화 실행

            if (CommunityBoardRegReducer_Result.n4ErrorCode !== 0) {

                const message = CommunityBoardRegReducer_Result.strErrorMessage;
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else if (!CommunityBoardRegReducer_Result.isSuccess) {

                const message = "게시물 등록 처리 중 알 수 없는 오류가 발생하였습니다.";
                setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            }
            else {

                const message = "게시물이 등록 되었습니다.";
                setToastMessageState(rawState => ({ ...rawState, isShow: true, message }));

                (window as any).urlChange("/community_board_list"); // url 주소 변경
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CommunityBoardRegReducer_Result]);

    /**
    * 제목 입력박스 change 이벤트
    * 
    * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
    * @returns {void}
    */
    const handlerTitleVal = (e: React.ChangeEvent<HTMLInputElement>): void => {

        setIptTitleVal(e.currentTarget.value);
    }

    /**
    * 별명 입력박스 change 이벤트
    * 
    * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
    * @returns {void}
    */
    const handlerNicknameVal = (e: React.ChangeEvent<HTMLInputElement>): void => {

        setIptNicknameVal(e.currentTarget.value);
    }

    /**
    * 별명 익명으로 표시 체크박스 change 이벤트
    * 
    * @param {React.ChangeEvent<HTMLInputElement>} e 이벤트 객체
    * @returns {void}
    */
    const handlerAnonymousVal = (e: React.ChangeEvent<HTMLInputElement>): void => {

        setIptAnonymousVal(e.currentTarget.checked);
    }

    /**
    * TextBox 폼 체크
    * @param {HTMLInputElement | null} refObj ref 객체
    * @param {string} name ref 객체명
    * @returns {boolean}
    */
    const TextBoxConfirm = (refObj: HTMLInputElement | null, name: string): boolean => {

        if (refObj?.value.trim() === "") {

            const message = name + " 입력하세요.";
            const callbackFuncOk = () => {
                refObj.value = "";
                refObj.focus();
            };
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message, callbackFuncOk }));

            return false;
        }
        return true;
    }

    /**
    * 등록 버튼 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerBoardReg = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        const txt_Reg_Title = $("#txt_Reg_Title");
        const txt_Reg_Nickname = $("#txt_Reg_Nickname");
        const txt_Reg_Content = $("#txt_Reg_Comment_One");

        const title = ((txt_Reg_Title.val() as string) + "").trim();
        const nickname = iptNicknameVal.trim();
        const content = ((txt_Reg_Content.val() as string) + "").trim();
        const anonymous_yn = iptAnonymousVal ? "Y" : "N";

        if (title === "") {
            TextBoxConfirm(txt_Reg_Title.get(0) as HTMLInputElement, "'제목'을");
            return;
        }

        if (txt_Reg_Nickname.is(":visible") && nickname === "") {
            TextBoxConfirm(txt_Reg_Nickname.get(0) as HTMLInputElement, "'별명'을");
            return;
        }
        if (content === "") {
            TextBoxConfirm(txt_Reg_Content.get(0) as HTMLInputElement, "'내용'을");
            return;
        }

        dispatch(asyncReqCommunityBoardReg({ title, nickname, content, anonymous_yn, file: (window as any).getPreviewFile("One") ?? undefined }));
    }

    /**
    * 취소 버튼 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerCancel = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        const message = "글 작성을 취소하시겠습니까?";
        const callbackFuncOk = () => {
            const prev_url = CommonUtil.getQuerystring("prev_url", "/community_board_list");
            (window as any).urlChange(prev_url); // url 주소 변경
        }
        setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: true, message, callbackFuncOk }));
    }

    /**
    * 이모지 표시 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerShowEmoji = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {

        // 이모지 영역 표시 or 숨김
        const root_emoji = $("#root_emoji_One");
        if (root_emoji.is(":visible")) root_emoji.hide();
        else root_emoji.show();

        root_emoji.find(".content_emoji").animate({ scrollTop: 0 }, 0); // 이모지 최상단으로 스크롤바 이동
    }

    return (
        <div className="container" id="div_info_ui" style={{ width: "100%" }}>

            <article className="bo_v" style={{ maxWidth: "100%", marginTop: "10px" }}>

                <header>
                    <h2 className="bo_v_title">
                        <input type="text" id="txt_Reg_Title" className="wr_name frm_input required" maxLength={100} placeholder="제목을 입력해주세요." style={{ width: "100%" }} value={iptTitleVal} onChange={handlerTitleVal} />
                    </h2>
                </header>

                <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_5", slot: "4410290707", height: CommonUtil.isMobileOs() ? 50 : 100 }}
                    kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_5", unit: "DAN-t4f3czp3pfe9", width: 320, height: 50 }}
                    marginStyle="10px 0 0"
                    refreshKey={ad_RefreshKey}
                />

                <section className="bo_v_atc">

                    <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

                    <input type="text" id="txt_Reg_Nickname" className="wr_name frm_input required" maxLength={15} placeholder="별명을 입력해주세요." value={iptNicknameVal} onChange={handlerNicknameVal} style={{ width: "100%", marginBottom: "10px", ...navigator.userAgent.indexOf(";UGGIYO_APP;") > -1 && NICKNAME === "" ? {} : { display: "none" } }} />

                    <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

                    <input type="checkbox" checked={iptAnonymousVal} style={{ width: "25px", height: "25px" }} onChange={handlerAnonymousVal} /> 별명을 '익명'으로 표시합니다.

                    <div style={{ width: "100%", height: "20px" }}>&nbsp;</div>

                    <div>
                        <input type="file" width="100%" accept="image/*" data-key={"One"} />
                        <div id="preview_root_One" style={{ display: "none" }}><Paper id="preview_One" sx={{ maxWidth: { md: "350px", xs: "100%" }, border: "1px solid #000000", marginTop: "10px" }} elevation={0} component="img" className="img_add_file" alt="" /></div>
                        <div style={{ color: "#ff762d", fontWeight: "bold", marginTop: "10px" }}>* 이미지 파일만 업로드 가능합니다.</div>
                    </div>

                    <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

                    <textarea
                        id="txt_Reg_Comment_One"
                        className="wr_name frm_input required"
                        maxLength={1000}
                        placeholder="내용을 입력해주세요."
                        style={{ width: "100%", height: "300px" }}
                    />

                    <div style={{ width: "100%", height: "10px" }}>&nbsp;</div>

                </section>

                <AdView googleAdsense={{ rootid: "adsense_Ad_Web_Root_6", slot: "3867365226", height: CommonUtil.isMobileOs() ? 50 : 100 }}
                    kakaoAdfit={{ rootid: "kakao_Ad_Web_Root_6", unit: "DAN-skjyt0f45mwk", width: 300, height: 250 }}
                    marginStyle="10px 0 0"
                    refreshKey={ad_RefreshKey}
                />

                <div style={{ width: "100%", height: "10px", float: "left" }}>&nbsp;</div>

                <div className="bo_v_top">

                    <a className="btn_submit" href="#" onClick={handlerCancel}>취소</a>
                    <a className="btn_submit" ref={refBtnReg} onClick={handlerBoardReg}>등록</a>
                    <button type="button" className="btn_emoji" onClick={handlerShowEmoji}>
                        <div>
                            <svg aria-label="이모티콘" fill="#262626" height="24" role="img" viewBox="0 0 48 48" width="24">
                                <path d="M24 48C10.8 48 0 37.2 0 24S10.8 0 24 0s24 10.8 24 24-10.8 24-24 24zm0-45C12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21S35.6 3 24 3z"></path>
                                <path d="M34.9 24c0-1.4-1.1-2.5-2.5-2.5s-2.5 1.1-2.5 2.5 1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5zm-21.8 0c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5-2.5-1.1-2.5-2.5zM24 37.3c-5.2 0-8-3.5-8.2-3.7-.5-.6-.4-1.6.2-2.1.6-.5 1.6-.4 2.1.2.1.1 2.1 2.5 5.8 2.5 3.7 0 5.8-2.5 5.8-2.5.5-.6 1.5-.7 2.1-.2.6.5.7 1.5.2 2.1 0 .2-2.8 3.7-8 3.7z"></path>
                            </svg>
                        </div>
                    </button>

                    <Emoji comment_seq="One" />

                </div>
                {
                    CommonUtil.isMobileOs() &&
                    <div style={{ height: "200px" }}>&nbsp;</div> // 앱 내에서 게시물 내용 입력 시, 관련 UI를 키패드가 가려버림.. 하단에 여유공간 확보!
                }

            </article >

        </div >
    )
}

export default BoardReg;
